/**
 * CrmConvertLeadModal — the cinematic "Begin conversion" experience, ported from conver_lead.html as
 * an in-workspace full-screen overlay (no separate route). Three acts:
 *   1. Opening screen — the lead's name, big, over a dark animated horizon.
 *   2. A 4-step wizard — Client (new/existing org), Contact (new/existing), fine-tune (name/type),
 *      confirm.
 *   3. A fading-phrase creation sequence → routes to the Opportunities tab.
 *
 * On confirm it writes a `Convert Lead` Record Update (services/recordUpdates) for the existing Zap to
 * create the Org/Contact/Opportunity + links + history. For NEW org/contact it first syncs any edited
 * name/email back onto the Lead (the Zap reads those from the Lead). It never creates the Project itself.
 *
 * Mount <CrmConvertLeadHost/> once (OutlineView); it listens for `open-convert-lead` { leadId } —
 * dispatched by the lead detail's "Begin conversion".
 */
import { useEffect, useRef, useState } from 'react'
import { getLeadDetail, updateLead } from '../../services/leads.js'
import { searchOrganizations, searchContacts, recordContactOrigin } from '../../services/contacts.js'
import { createConvertLeadUpdate, PROJECT_TYPES, BUILDING_TYPES } from '../../services/recordUpdates.js'
import { alertDialog } from '../Dialog.jsx'

const PHRASES = [
  'Opening a new opportunity', 'Capturing what we know', 'Noting project scope',
  'Building a pursuit record', 'Preserving conversation history',
  'Preparing a proposal workspace', 'Everything in one place',
]

/* ── Host (mounted once) ─────────────────────────────────────────────────── */
export function CrmConvertLeadHost() {
  const [leadId, setLeadId] = useState(null)
  useEffect(() => {
    function onOpen(e) { const id = e && e.detail && e.detail.leadId; if (id) setLeadId(id) }
    window.addEventListener('open-convert-lead', onOpen)
    return () => window.removeEventListener('open-convert-lead', onOpen)
  }, [])
  if (!leadId) return null
  return <CrmConvertLeadModal leadId={leadId} onClose={() => setLeadId(null)} />
}

export default function CrmConvertLeadModal({ leadId, onClose }) {
  const [lead, setLead] = useState(null)
  const [phase, setPhase] = useState('intro')   // intro | wizard | cinematic
  const [step, setStep] = useState(0)
  const [sceneShown, setSceneShown] = useState(false)   // dark scene (canvas/vignette) fades up on mount
  const [textShown, setTextShown] = useState(false)     // opening title blurs/rises in once the lead loads

  // wizard state
  const [orgMode, setOrgMode] = useState(null)       // 'new' | 'existing'
  const [orgId, setOrgId] = useState(null)
  const [orgName, setOrgName] = useState('')
  const [orgResults, setOrgResults] = useState(null)
  const [contactMode, setContactMode] = useState(null)
  const [contactId, setContactId] = useState(null)
  const [contactName, setContactName] = useState('')
  const [ctFirst, setCtFirst] = useState('')
  const [ctLast, setCtLast] = useState('')
  const [ctEmail, setCtEmail] = useState('')
  const [ctResults, setCtResults] = useState(null)
  const [projectName, setProjectName] = useState('')
  const [projectType, setProjectType] = useState('')
  const [buildingType, setBuildingType] = useState('')
  const [phrase, setPhrase] = useState(0)

  // The dark scene fades up immediately; the title waits for the lead so it reveals fully-formed.
  useEffect(() => {
    const t = setTimeout(() => setSceneShown(true), 40)
    return () => clearTimeout(t)
  }, [])

  useEffect(() => {
    getLeadDetail(leadId).then(l => {
      if (l) {
        setLead(l)
        const base = l.organization || (l.lastName ? `${l.lastName} Residence` : (l.name || ''))
        setProjectName(base)
        setOrgName(l.organization || (l.lastName ? `${l.lastName} Family` : ''))
        setCtFirst(l.firstName || ''); setCtLast(l.lastName || ''); setCtEmail(l.email || '')
      }
      // Reveal the title shortly after the scene has begun materializing (min ~500ms for drama).
      setTimeout(() => setTextShown(true), 520)
    }).catch(() => { setTimeout(() => setTextShown(true), 520) })
  }, [leadId])

  // Staged blur/rise entrance for the opening title elements.
  function enter(delay, extra) {
    return {
      opacity: textShown ? 1 : 0,
      transform: textShown ? 'translateY(0)' : 'translateY(12px)',
      filter: textShown ? 'blur(0px)' : 'blur(10px)',
      transition: `opacity 1.4s ease ${delay}s, transform 1.4s ease ${delay}s, filter 1.4s ease ${delay}s, letter-spacing 1.6s ease ${delay}s`,
      ...(extra || {}),
    }
  }

  const location = lead ? [lead.city, lead.state].filter(Boolean).join(', ') : ''
  const projTitle = (lead && (lead.organization || (lead.lastName ? `${lead.lastName} Residence` : lead.name))) || 'New opportunity'

  const emailOk = !ctEmail || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ctEmail.trim())
  const canNext = (
    step === 0 ? (orgMode === 'new' ? !!orgName.trim() : orgMode === 'existing' ? !!orgId : false) :
    step === 1 ? (contactMode === 'new' ? (!!ctFirst.trim() && !!ctLast.trim() && emailOk) : contactMode === 'existing' ? !!contactId : false) :
    step === 2 ? (!!projectName.trim() && !!projectType && !!buildingType) :
    true
  )

  async function searchOrg(v) {
    if (!v.trim()) { setOrgResults(null); return }
    try { const r = await searchOrganizations(v); setOrgResults((r.contacts || []).slice(0, 8)) } catch { setOrgResults([]) }
  }
  async function searchCt(v) {
    if (!v.trim()) { setCtResults(null); return }
    try { const r = await searchContacts(v); setCtResults((r.contacts || []).slice(0, 8)) } catch { setCtResults([]) }
  }

  function confirm() {
    setPhase('cinematic')
    // Fire the writes; the cinematic plays regardless of result (matches the legacy).
    ;(async () => {
      try {
        if (orgMode === 'new' && orgName.trim() && orgName.trim() !== (lead.organization || '')) {
          await updateLead(leadId, { organization: orgName.trim() })
        }
        if (contactMode === 'new') {
          const patch = {}
          if (ctFirst && ctFirst !== lead.firstName) patch.firstName = ctFirst
          if (ctLast && ctLast !== lead.lastName) patch.lastName = ctLast
          if (ctEmail && ctEmail !== lead.email) patch.email = ctEmail
          if (Object.keys(patch).length) await updateLead(leadId, patch)
        }
        // The automation names the new organization from the LEAD's Organization field, so a name
        // that didn't save there becomes an unnamed organization (Hueston, 2026-08-11). Read the
        // lead back and stop before anything is created if the name isn't on it.
        if (orgMode === 'new') {
          const saved = await getLeadDetail(leadId)
          if (!saved || !String(saved.organization || '').trim()) {
            throw new Error(`The client name "${orgName.trim()}" didn't save to the lead, so nothing was converted. Try again.`)
          }
        }
        await createConvertLeadUpdate({ leadId, orgMode, orgId, contactMode, contactId, opportunityName: projectName, projectType, buildingType })
        recordContactOrigin('lead', leadId, 'lead_conversion', projectName)
      } catch (e) {
        // The cinematic already plays regardless — surface the failure after it finishes rather
        // than blocking the sequence, since the write is what actually creates the opportunity.
        await alertDialog({ title: 'Conversion failed', message: (e && e.message) || 'Could not convert the lead — the opportunity was not created. Please try again.' })
      }
    })()
  }

  // Cinematic phrase ticker → finish.
  useEffect(() => {
    if (phase !== 'cinematic') return
    if (phrase < PHRASES.length) {
      const t = setTimeout(() => setPhrase(p => p + 1), 1900)
      return () => clearTimeout(t)
    }
    // final hold, then route to Opportunities + close
    const t = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('crm-lead-converted', { detail: { leadId } }))
      window.dispatchEvent(new CustomEvent('open-crm', { detail: { tool: 'opportunities' } }))
      onClose && onClose()
    }, 2600)
    return () => clearTimeout(t)
  }, [phase, phrase, leadId, onClose])

  return (
    <div style={S.overlay}>
      <HorizonCanvas shown={sceneShown} />
      <div style={{ ...S.vignette, opacity: sceneShown ? 1 : 0, transition: 'opacity 1.6s ease' }} />

      {phase === 'intro' && (
        <div style={{ ...S.center, transform: textShown ? 'scale(1)' : 'scale(1.04)', transition: 'transform 2.2s ease' }}>
          <div style={enter(0.1, S.eyebrow)}>New opportunity</div>
          <div style={enter(0.45, { ...S.bigName, letterSpacing: textShown ? '0.18em' : '0.34em' })}>{projTitle.toUpperCase()}</div>
          {location && <div style={enter(0.95, S.loc)}>{location.toUpperCase()}</div>}
          <button style={enter(1.5, S.cta)} onClick={() => setPhase('wizard')} disabled={!lead}>Begin conversion</button>
          <button style={enter(1.8, S.dismiss)} onClick={onClose}>Cancel</button>
        </div>
      )}

      {phase === 'wizard' && (
        <div style={S.dialogWrap}>
          <div style={S.dialog}>
            <div style={S.dots}>{[0, 1, 2, 3].map(i => <span key={i} style={{ ...S.dot, ...(i === step ? S.dotActive : i < step ? S.dotDone : {}) }} />)}</div>
            <div style={S.dStep}>Step {step + 1} of 4</div>

            {step === 0 && (
              <>
                <div style={S.dTitle}>Is this a new client or an existing one?</div>
                <div style={S.dSub}>Whether this household or organization already exists in the system.</div>
                <div style={S.toggleRow}>
                  <button style={tog(orgMode === 'new')} onClick={() => { setOrgMode('new'); setOrgId(null) }}>New client</button>
                  <button style={tog(orgMode === 'existing')} onClick={() => { setOrgMode('existing') }}>Existing client</button>
                </div>
                {orgMode === 'new' && (
                  <Field label="Household or organization name">
                    <input style={S.input} value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="e.g. Smith Family" />
                  </Field>
                )}
                {orgMode === 'existing' && (
                  <Field label="Search existing clients">
                    <input style={S.input} placeholder="Type to search…" onChange={e => searchOrg(e.target.value)} />
                    <Results items={orgResults} render={r => ({ name: r.fields['Organization Name'] || r.id, sub: r.fields['Client City'] || '' })}
                      selectedId={orgId} onPick={r => { setOrgId(r.id); setOrgName(r.fields['Organization Name'] || ''); setOrgResults(null) }} />
                    {orgId && <div style={S.picked}>Selected: {orgName}</div>}
                  </Field>
                )}
              </>
            )}

            {step === 1 && (
              <>
                <div style={S.dTitle}>Is this a new contact or an existing one?</div>
                <div style={S.dSub}>The primary point of contact for the project.</div>
                <div style={S.toggleRow}>
                  <button style={tog(contactMode === 'new')} onClick={() => { setContactMode('new'); setContactId(null) }}>New contact</button>
                  <button style={tog(contactMode === 'existing')} onClick={() => { setContactMode('existing') }}>Existing contact</button>
                </div>
                {contactMode === 'new' && (
                  <>
                    <div style={S.grid2}>
                      <Field label="First name"><input style={S.input} value={ctFirst} onChange={e => setCtFirst(e.target.value)} /></Field>
                      <Field label="Last name"><input style={S.input} value={ctLast} onChange={e => setCtLast(e.target.value)} /></Field>
                    </div>
                    <Field label="Email"><input style={{ ...S.input, borderColor: emailOk ? undefined : 'rgba(220,120,120,0.7)' }} type="email" value={ctEmail} onChange={e => setCtEmail(e.target.value)} /></Field>
                  </>
                )}
                {contactMode === 'existing' && (
                  <Field label="Search existing contacts">
                    <input style={S.input} placeholder="Type to search…" onChange={e => searchCt(e.target.value)} />
                    <Results items={ctResults} render={r => ({ name: (r.fields['Name'] || r.id).trim(), sub: r.fields['Email'] || '' })}
                      selectedId={contactId} onPick={r => { setContactId(r.id); setContactName((r.fields['Name'] || '').trim()); setCtResults(null) }} />
                    {contactId && <div style={S.picked}>Selected: {contactName}</div>}
                  </Field>
                )}
              </>
            )}

            {step === 2 && (
              <>
                <div style={S.dTitle}>Fine-tune the opportunity.</div>
                <div style={S.dSub}>Set the project name, type, and building type.</div>
                <Field label="Project name"><input style={S.input} value={projectName} onChange={e => setProjectName(e.target.value)} /></Field>
                <div style={S.grid2}>
                  <Field label="Project type">
                    <select style={S.input} value={projectType} onChange={e => setProjectType(e.target.value)}>
                      <option value="" style={S.option}>Select one</option>{PROJECT_TYPES.map(t => <option key={t} value={t} style={S.option}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Building type">
                    <select style={S.input} value={buildingType} onChange={e => setBuildingType(e.target.value)}>
                      <option value="" style={S.option}>Select one</option>{BUILDING_TYPES.map(t => <option key={t} value={t} style={S.option}>{t}</option>)}
                    </select>
                  </Field>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div style={S.dTitle}>Confirm and create.</div>
                <div style={S.dSub}>Review before creating the opportunity.</div>
                <div style={S.summary}>
                  <SumRow k="Project name" v={projectName} />
                  <SumRow k="Client" v={`${orgName}${orgMode === 'new' ? ' (new)' : ' (existing)'}`} />
                  <SumRow k="Contact" v={contactMode === 'new' ? `${ctFirst} ${ctLast} (new)`.trim() : `${contactName} (existing)`} />
                  <SumRow k="Project type" v={projectType || '—'} />
                  <SumRow k="Building type" v={buildingType || '—'} />
                </div>
              </>
            )}

            <div style={S.nav}>
              <button style={S.back} onClick={() => (step === 0 ? setPhase('intro') : setStep(s => s - 1))}>Back</button>
              <div style={{ flex: 1 }} />
              {step < 3
                ? <button style={S.next} onClick={() => setStep(s => s + 1)} disabled={!canNext}>{step === 2 ? 'Review' : 'Continue'}</button>
                : <button style={S.next} onClick={confirm}>Confirm and proceed</button>}
            </div>
          </div>
        </div>
      )}

      {phase === 'cinematic' && (
        <div style={S.center}>
          {phrase < PHRASES.length ? (
            <div key={phrase} style={S.phrase}>{PHRASES[phrase]}</div>
          ) : (
            <>
              <div style={S.finalName}>{(projectName || projTitle).toUpperCase()}</div>
              <div style={S.finalSub}>Ready to pursue.</div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

/* ── Small pieces ────────────────────────────────────────────────────────── */
function Field({ label, children }) {
  return <div style={S.fg}><label style={S.fl}>{label}</label>{children}</div>
}
function SumRow({ k, v }) {
  return <div style={S.sumRow}><div style={S.sumKey}>{k}</div><div style={S.sumVal}>{v}</div></div>
}
function Results({ items, render, selectedId, onPick }) {
  if (items === null) return null
  if (!items.length) return <div style={S.noRes}>No matches</div>
  return (
    <div style={S.results}>
      {items.map(r => {
        const { name, sub } = render(r)
        return (
          <button key={r.id} style={{ ...S.resItem, ...(r.id === selectedId ? S.resItemSel : {}) }} onClick={() => onPick(r)}>
            <span><span style={S.resName}>{name}</span>{sub ? <span style={S.resSub}>{sub}</span> : null}</span>
            {r.id === selectedId && <span style={S.resCheck}>✓</span>}
          </button>
        )
      })}
    </div>
  )
}

/* ── Background canvas (rising mist over a horizon) ──────────────────────── */
function HorizonCanvas({ shown }) {
  const ref = useRef(null)
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return
    const cx = canvas.getContext('2d')
    let W, H, particles, time = 0, raf
    function resize() { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight }
    function rnd(a, b) { return a + Math.random() * (b - a) }
    function mk() {
      return { x: rnd(0, W), y: H * 0.42 + rnd(0, H * 0.45), vy: -rnd(0.14, 0.52), vx: rnd(-0.13, 0.13),
        life: 0, maxLife: rnd(220, 500), size: rnd(55, 255), grow: rnd(0.12, 0.3), layer: Math.floor(Math.random() * 3) }
    }
    function reset(p) { Object.assign(p, mk()) }
    const COLORS = [[195, 220, 238], [150, 192, 220], [108, 162, 200]]
    function draw() {
      raf = requestAnimationFrame(draw); time++
      // horizon
      const hy = H * (0.48 + (Math.sin(time * 0.0004) + 1) / 2 * 0.06)
      const g = cx.createLinearGradient(0, 0, 0, H)
      g.addColorStop(0, '#041518'); g.addColorStop(hy / H * 0.6, '#07272D'); g.addColorStop(hy / H, '#0a3540'); g.addColorStop(1, '#07272D')
      cx.fillStyle = g; cx.fillRect(0, 0, W, H)
      const gg = cx.createRadialGradient(W * 0.5, hy, 0, W * 0.5, hy, W * 0.55)
      gg.addColorStop(0, 'rgba(15,80,90,0.22)'); gg.addColorStop(0.5, 'rgba(7,50,60,0.08)'); gg.addColorStop(1, 'rgba(0,0,0,0)')
      cx.fillStyle = gg; cx.fillRect(0, 0, W, H)
      for (const p of particles) {
        p.x += p.vx; p.y += p.vy; p.vy *= 0.9985; p.life++
        if (p.life > p.maxLife || p.y < -p.size * 2) reset(p)
        const t = p.life / p.maxLife
        const peak = 0.11 + (p.layer === 0 ? 0.04 : 0)
        const alpha = t < 0.12 ? (t / 0.12) * peak : t > 0.65 ? ((1 - t) / 0.35) * peak : peak
        const size = p.size + p.life * p.grow
        const c = COLORS[p.layer]
        const rg = cx.createRadialGradient(p.x, p.y, 0, p.x, p.y, size)
        rg.addColorStop(0, `rgba(${c[0]},${c[1]},${c[2]},${alpha})`)
        rg.addColorStop(0.5, `rgba(${c[0]},${c[1]},${c[2]},${alpha * 0.4})`)
        rg.addColorStop(1, `rgba(${c[0]},${c[1]},${c[2]},0)`)
        cx.beginPath(); cx.arc(p.x, p.y, size, 0, Math.PI * 2); cx.fillStyle = rg; cx.fill()
      }
    }
    resize(); particles = Array.from({ length: 70 }, () => { const p = mk(); p.life = Math.floor(Math.random() * p.maxLife); return p })
    draw()
    window.addEventListener('resize', resize)
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize) }
  }, [])
  return <canvas ref={ref} style={{ ...S.canvas, opacity: shown ? 1 : 0, transition: 'opacity 1.8s ease' }} />
}

/* ── Styles ──────────────────────────────────────────────────────────────── */
function tog(on) {
  return { flex: 1, padding: '13px 16px', borderRadius: 6, fontSize: 13, cursor: 'pointer', textAlign: 'center',
    fontFamily: '"DM Sans", sans-serif', background: on ? 'rgba(255,255,255,0.1)' : 'transparent',
    border: `0.5px solid ${on ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.15)'}`, color: on ? '#fff' : 'rgba(255,255,255,0.6)' }
}

const WHITE = (a) => `rgba(255,255,255,${a})`
const S = {
  overlay: { position: 'fixed', inset: 0, zIndex: 700, background: '#07272D', color: '#fff', overflow: 'hidden', fontFamily: '"DM Sans", sans-serif' },
  canvas: { position: 'fixed', inset: 0, width: '100%', height: '100%', zIndex: 0 },
  vignette: { position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none', background: 'radial-gradient(ellipse at center, transparent 30%, rgba(7,39,45,0.55) 70%, rgba(7,39,45,0.88) 100%)' },
  center: { position: 'fixed', inset: 0, zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24 },
  eyebrow: { fontSize: 11, fontWeight: 500, letterSpacing: '0.18em', textTransform: 'uppercase', color: WHITE(0.4), marginBottom: 18 },
  bigName: { fontFamily: '"Montserrat", sans-serif', fontSize: 'clamp(30px,6vw,52px)', fontWeight: 200, color: WHITE(0.92), lineHeight: 1.1, letterSpacing: '0.18em', textTransform: 'uppercase' },
  loc: { fontSize: 14, color: WHITE(0.38), marginTop: 14, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 300 },
  cta: { marginTop: 48, padding: '13px 32px', fontSize: 12, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', fontFamily: '"DM Sans", sans-serif', background: 'transparent', color: WHITE(0.75), border: `0.5px solid ${WHITE(0.25)}`, borderRadius: 2, cursor: 'pointer' },
  dismiss: { marginTop: 18, fontSize: 11, color: WHITE(0.3), background: 'none', border: 'none', cursor: 'pointer', letterSpacing: '0.06em' },

  dialogWrap: { position: 'fixed', inset: 0, zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  dialog: { background: 'rgba(7,39,45,0.9)', border: `0.5px solid ${WHITE(0.1)}`, borderRadius: 12, padding: '30px 26px', width: '100%', maxWidth: 440, backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' },
  dots: { display: 'flex', gap: 6, marginBottom: 18 },
  dot: { width: 5, height: 5, borderRadius: '50%', background: WHITE(0.2) },
  dotActive: { background: WHITE(0.8) },
  dotDone: { background: WHITE(0.4) },
  dStep: { fontSize: 10, fontWeight: 500, color: WHITE(0.3), textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8, fontFamily: '"DM Mono", monospace' },
  dTitle: { fontFamily: '"Montserrat", sans-serif', fontSize: 21, fontWeight: 300, color: WHITE(0.9), lineHeight: 1.3, marginBottom: 5 },
  dSub: { fontSize: 13, color: WHITE(0.4), lineHeight: 1.5, marginBottom: 22 },
  toggleRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  fg: { marginBottom: 14 },
  fl: { display: 'block', fontSize: 10, fontWeight: 500, color: WHITE(0.35), textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 7 },
  input: { width: '100%', padding: '11px 13px', fontSize: 14, fontFamily: '"DM Sans", sans-serif', background: WHITE(0.06), border: `0.5px solid ${WHITE(0.15)}`, borderRadius: 6, color: WHITE(0.9), outline: 'none', boxSizing: 'border-box' },
  // Native <option> popups render as an opaque OS-drawn list that ignores the translucent
  // WHITE(0.06) panel background — without an explicit solid color here, the near-white text
  // color inherited from S.input lands on the browser's own white dropdown, invisible except
  // on the one row the OS auto-highlights on hover/focus.
  option: { background: '#07272D', color: '#fff' },
  results: { marginTop: 6, border: `0.5px solid ${WHITE(0.12)}`, borderRadius: 6, overflow: 'hidden', maxHeight: 220, overflowY: 'auto' },
  resItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '10px 13px', fontSize: 13, color: WHITE(0.7), cursor: 'pointer', background: 'none', border: 'none', borderBottom: `0.5px solid ${WHITE(0.07)}`, textAlign: 'left' },
  resItemSel: { background: WHITE(0.1), color: '#fff' },
  resName: { display: 'block' },
  resSub: { display: 'block', fontSize: 11, color: WHITE(0.35), marginTop: 2 },
  resCheck: { color: WHITE(0.6) },
  noRes: { marginTop: 6, padding: '10px 13px', fontSize: 12, color: WHITE(0.3), fontStyle: 'italic', border: `0.5px solid ${WHITE(0.1)}`, borderRadius: 6 },
  picked: { marginTop: 8, fontSize: 12, color: WHITE(0.6) },
  summary: { background: WHITE(0.04), border: `0.5px solid ${WHITE(0.1)}`, borderRadius: 8, padding: 16 },
  sumRow: { display: 'flex', gap: 12, padding: '7px 0', borderBottom: `0.5px solid ${WHITE(0.07)}` },
  sumKey: { fontSize: 11, color: WHITE(0.3), minWidth: 92, flexShrink: 0 },
  sumVal: { fontSize: 13, color: WHITE(0.88) },
  nav: { display: 'flex', alignItems: 'center', marginTop: 22, paddingTop: 18, borderTop: `0.5px solid ${WHITE(0.08)}` },
  back: { fontSize: 12, color: WHITE(0.4), background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: '"DM Sans", sans-serif', padding: 0 },
  next: { padding: '11px 24px', fontSize: 12, fontWeight: 500, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: '"DM Sans", sans-serif', background: WHITE(0.9), color: '#07272D', border: 'none', borderRadius: 4, cursor: 'pointer' },

  phrase: { fontFamily: '"Montserrat", sans-serif', fontSize: 'clamp(15px,2.4vw,21px)', fontWeight: 300, letterSpacing: '0.1em', color: WHITE(0.72), animation: 'none' },
  finalName: { fontFamily: '"Montserrat", sans-serif', fontSize: 'clamp(24px,4.5vw,42px)', fontWeight: 200, letterSpacing: '0.2em', textTransform: 'uppercase', color: WHITE(0.92) },
  finalSub: { fontFamily: '"Montserrat", sans-serif', fontSize: 11, fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase', color: WHITE(0.32), marginTop: 18 },
}
